import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { gengql } from '@/__generated__';
import { GroupRole } from '@/__generated__/graphql';

// Team Activity is sourced from the team roster (groupMembers), NOT billing_seats:
// seats only exist for SMB accounts, so the old seatAllocation source left the card
// empty for credit/enterprise teams (ENG-5257). groupMembers also carries real
// per-member activity stats, so we can drop the placeholder "member since" heuristic.
const DASHBOARD_TEAM_MEMBERS = gengql(/* GraphQL */ `
  query dashboardTeamMembers($groupId: String!) {
    groupMembers(group_id: $groupId, limit: 1000, states: [active]) {
      edges {
        node {
          id
          role
          consultation_count
          hasUpcomingConsultations
          user {
            id
            name
            first_name
            last_name
            picture_url
          }
        }
      }
    }
  }
`);

export interface TeamMemberInfo {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  pictureUrl: string | null;
  role: GroupRole;
  consultationCount: number;
  hasUpcomingConsultations: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useDashboardTeamMembers(groupId: string | undefined, enabled: boolean) {
  const { data, loading, error } = useQuery(DASHBOARD_TEAM_MEMBERS, {
    variables: { groupId: groupId! },
    skip: !groupId || !enabled,
    fetchPolicy: 'cache-and-network',
  });

  const members = useMemo((): TeamMemberInfo[] | null => {
    const edges = data?.groupMembers?.edges;
    if (!edges) return null;

    return edges
      .map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => !!node?.user)
      .map((node) => ({
        id: node.id,
        name: node.user?.name ?? '',
        firstName: node.user?.first_name ?? '',
        lastName: node.user?.last_name ?? '',
        pictureUrl: node.user?.picture_url ?? null,
        role: node.role,
        consultationCount: node.consultation_count,
        hasUpcomingConsultations: node.hasUpcomingConsultations,
      }));
  }, [data]);

  return { members, loading, error };
}
